import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Text, NewSeat } from '@reservamos/elements';
import moment from 'moment';
import cn from 'utils/cn';

/**
 * QuickPurchaseTicketSummary component.
 * Render a ticket-shaped card displaying passenger summary information
 * including name, category, document, seat assignment, and additional details.
 * Secondary details (email, phone, date of birth, gender, nationality) are hidden
 * behind a collapsible "see more" toggle.
 * @param {object} props - name, category, categoryIcon, documentType, documentId, seatNumber, returnSeatNumber, email, phone, dateOfBirth, gender, nationality.
 * @param {string} props.name - Full passenger name.
 * @param {string} props.category - Translated category label.
 * @param {string} props.categoryIcon - Avatar image URL for the passenger type.
 * @param {string} props.documentType - Document type label (already translated).
 * @param {string} props.documentId - Document ID number.
 * @param {string} props.seatNumber - Departure seat number.
 * @param {string} props.returnSeatNumber - Return seat number.
 * @param {string} props.email - Passenger email address.
 * @param {string} props.phone - Passenger phone number.
 * @param {string} props.dateOfBirth - Passenger date of birth in YYYY-MM-DD format.
 * @param {string} props.gender - Passenger gender ('male' or 'female').
 * @param {string} props.nationality - Passenger nationality display label.
 */
const QuickPurchaseTicketSummary = ({
  name,
  category,
  categoryIcon,
  documentType,
  documentId,
  seatNumber,
  returnSeatNumber,
  email,
  phone,
  dateOfBirth,
  gender,
  nationality,
}) => {
  const { t } = useTranslation(['purchase', 'general', 'passengers']);
  const [isExpanded, setIsExpanded] = useState(false);

  const hasCollapsibleContent = email || phone || dateOfBirth || gender || nationality;

  return (
    <div
      className={cn(
        'relative bg-white rounded-2xl overflow-visible shadow-md w-full sm:w-[250px]',
        "before:content-[''] before:absolute before:top-[35%] before:-translate-y-1/2 before:left-0 before:-translate-x-1/2 before:w-5 before:h-5 before:rounded-full before:z-[11] before:bg-white before:shadow-inner",
        "after:content-[''] after:absolute after:top-[35%] after:-translate-y-1/2 after:right-0 after:translate-x-1/2 after:w-5 after:h-5 after:rounded-full after:z-[11] after:bg-white after:shadow-inner",
      )}
    >
      <div className="flex flex-col justify-between p-4 h-full">
        <Text weight="bold" size="M">
          {name}
        </Text>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div>
              <Text size="S" color="grayMedium">
                {t('purchase:quick_checkout.category')}:
              </Text>
              <Text size="S" color="primary" weight="semibold">
                {category}
              </Text>
            </div>
            {categoryIcon && (
              <div className="flex items-center justify-center rounded-full bg-primary-regular/40 p-1">
                <img src={categoryIcon} alt="" className="w-8 h-8" />
              </div>
            )}
          </div>

          {documentType && documentId && (
            <div>
              <Text size="S" color="grayMedium">
                {documentType}:
              </Text>
              <Text size="S" color="grayMedium" weight="semibold">
                {documentId}
              </Text>
            </div>
          )}

          {seatNumber && (
            <div className="flex items-center justify-between gap-2">
              <Text size="S" color="grayMedium">
                {t('purchase:quick_checkout.seat')}
              </Text>
              <NewSeat isSmall type="selected" number={seatNumber} />
            </div>
          )}

          {returnSeatNumber && (
            <div className="flex items-center justify-between gap-2">
              <Text size="S" color="grayMedium">
                {t('purchase:quick_checkout.return_seat')}
              </Text>
              <NewSeat isSmall type="selected" number={returnSeatNumber} />
            </div>
          )}

          {hasCollapsibleContent && (
            <>
              <div
                className={cn(
                  'flex flex-col gap-3 overflow-hidden transition-all duration-500 ease-in-out',
                  isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0',
                )}
              >
                {email && (
                  <div>
                    <Text size="S" color="grayMedium">
                      {t('purchase:quick_checkout.email')}:
                    </Text>
                    <Text size="S" color="grayMedium" weight="semibold">
                      {email}
                    </Text>
                  </div>
                )}

                {phone && (
                  <div>
                    <Text size="S" color="grayMedium">
                      {t('purchase:quick_checkout.phone')}:
                    </Text>
                    <Text size="S" color="grayMedium" weight="semibold">
                      {phone}
                    </Text>
                  </div>
                )}

                {dateOfBirth && (
                  <div>
                    <Text size="S" color="grayMedium">
                      {t('purchase:quick_checkout.date_of_birth')}:
                    </Text>
                    <Text size="S" color="grayMedium" weight="semibold">
                      {moment(dateOfBirth).format('DD/MM/YYYY')}
                    </Text>
                  </div>
                )}

                {gender && (
                  <div>
                    <Text size="S" color="grayMedium">
                      {t('purchase:quick_checkout.gender')}:
                    </Text>
                    <Text size="S" color="grayMedium" weight="semibold">
                      {t(`general:${gender}`)}
                    </Text>
                  </div>
                )}

                {nationality && (
                  <div>
                    <Text size="S" color="grayMedium">
                      {t('passengers:nationality')}:
                    </Text>
                    <Text size="S" color="grayMedium" weight="semibold">
                      {nationality}
                    </Text>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => setIsExpanded((prev) => !prev)}
                className="text-xs text-primary-regular hover:text-primary-strong transition-colors duration-200 self-start"
              >
                {isExpanded
                  ? t('purchase:quick_checkout.see_less')
                  : t('purchase:quick_checkout.see_more')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

QuickPurchaseTicketSummary.propTypes = {
  name: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  categoryIcon: PropTypes.string,
  documentType: PropTypes.string,
  documentId: PropTypes.string,
  seatNumber: PropTypes.string,
  returnSeatNumber: PropTypes.string,
  email: PropTypes.string,
  phone: PropTypes.string,
  dateOfBirth: PropTypes.string,
  gender: PropTypes.string,
  nationality: PropTypes.string,
};

QuickPurchaseTicketSummary.defaultProps = {
  categoryIcon: null,
  documentType: null,
  documentId: null,
  seatNumber: null,
  returnSeatNumber: null,
  email: null,
  phone: null,
  dateOfBirth: null,
  gender: null,
  nationality: null,
};

export default QuickPurchaseTicketSummary;
